import { Card, Divider, Grid } from '@mui/material';
import { Button, DatePicker, Form, Typography, BrandIcon } from '@uhc-tempo/components';
import '@uhc-tempo/components/tempo.css';
import { IconPillBot } from '@uhc-tempo/icons';
import { Launch } from '@mui/icons-material';
import moment from 'moment';
import { useContext, useEffect, useState, useRef } from 'react';
import { FormContext } from '../../../context/Form.context';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import DisclaimerModal from './DisclaimerModal.component';
import GeneralSnackBar from '../../home/components/Snackbar';
import LinearIndeterminateProgress from '../../home/components/LinearIndeterminateProgress';
import getMemberDetails from '../../../API/MemberBenefitsService';
import { useHistory } from 'react-router-dom';
import DisclaimerService from '../../../API/DisclaimerService';
import FeaturesService from '../../../API/FeaturesService';
import Cookies from 'js-cookie';
import { getAccessToken } from '../../../utils/Auth.util';
import { getAPI } from '../../../utils/ApiFunctions';
import { SEARCH_ROUTE } from '../../../constants';
import useDetectMobile from '../../../utils/customHooks/useDetectMobile';
import { getMicrofrontendStargate } from '../../../utils/microFrontendStargateToken';
import { apm } from '@elastic/apm-rum';

const MemberPage = (props) => {
    const classes = styles(theme);
    const [showDisclaimers, setShowDisclaimers] = useState(false);
    const [disableLookMeUpButton, setDisableLookMeUpButton] = useState(true);
    const [checkboxSelected, setCheckboxSelected] = useState(false);
    const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
    const currentDate = moment().format('YYYY-MM-DD');
    const history = useHistory();
    const ref = useRef(null);
    const isDesktopView = useDetectMobile(ref);

    // Snackbar & loading state variables
    const [memberDetailsErrorSnackbarOpen, setMemberDetailsErrorSnackbarOpen] = useState(false);
    const [memberDetailsErrorMessage, setMemberDetailsErrorMessage] = useState('');
    const [linearProgressDisplay, setLinearProgressDisplay] = useState(false);

    // Manual page load apm transaction, used for page load metrics
    const transaction = apm.startTransaction('Login Page', 'Component Page Load');

    const {
        formValues,
        handleChange,
        handleChangeCustom,
        noMemberFound,
        validateFormFieldsOnSubmit,
        addMemberDetailsToContext,
        validateFormData,
        setNoMemberFound,
        setIntegrationType,
        rxToggle,
        setRxToggle,
        setCpttCorrelationId,
        sanitizeMemberFormData,
        setSourceSystem,
        resetMemberDetails,
        setPlanBenefits,
    } = useContext(FormContext);

    const {
        firstName,
        lastName,
        groupNumber,
        memberNumber,
        dateOfBirth,
        disclaimerCheck,
        defaultDateOfBirth,
    } = formValues;

    const validateRequiredFields = (metadata) => {
        if (
            !('integrationType' in metadata) ||
            !('sourceSystem' in metadata) ||
            !metadata['integrationType'] ||
            !metadata['sourceSystem']
        ) {
            return false;
        }
        return true;
    };

    // autopopulates the member info form & sets integration type based on metadata passed from consuming portal/container
    const updateFormValue = (metadata) => {
        if (!validateRequiredFields(metadata)) {
            return;
        }
        for (let key in metadata) {
            if (formValues[key] && metadata[key]) {
                if (key == 'dateOfBirth') {
                    handleChangeCustom(metadata[key], 'dateOfBirth');
                    handleChangeCustom(metadata[key], 'defaultDateOfBirth');
                } else {
                    handleChange({
                        target: { type: 'text', name: key, value: metadata[key] },
                    });
                }
            }
            if (key == 'integrationType') {
                setIntegrationType(metadata[key]);
            }
            if (key == 'sourceSystem') {
                setSourceSystem(metadata[key]);
            }
            if (key == 'rxToggle') {
                setRxToggle(metadata[key]);
            }
        }
    };

    /*  
        Possible Logic Scenarios
        1. { no metadata } => Go to Big 5
        2. { big 5=blank, sourceSystem, integrationType=member}  => Go to Big 5
        3. { big 5=blank, sourceSystem, integrationType=advocate} => Go to Big 5 
        4. { big 5=available, sourceSystem, integrationType=advocate} => Go to Big 5 pre-populated
        5. { big 5=available, sourceSystem, integrationType=member } => Go to Big 5 pre-populated

        checks for portal integration patterns 
    */
    useEffect(async () => {
        resetMemberDetails();
        //to fetch the microui stargate token for the first time. not using here but fetching it to be used later.
        await getMicrofrontendStargate();
        const token = await getAccessToken('Stargate_token');

        DisclaimerService.getGenericDisclaimers().then(({ data }) => {
            handleChangeCustom(data, 'termsAndConditions');
        });

        // fetch feature flags for microfronend and store them in local storage
        FeaturesService.getFeatures(token).then(({ data }) => {
            localStorage.setItem(
                'x-epe-features',
                JSON.stringify({
                    value: data?.features,
                    expiry: new Date().getTime() + 86400,
                })
            );
        });

        const queryParams = location.search.replace('?', '').split(/[=&]+/);

        if (props.integrationType && props.sourceSystem) {
            updateFormValue(props);
        } else if (props.token) {
            const meta = await getAPI(
                `${process.env.TOKENS_URL}/${encodeURIComponent(props.token)}`,
                null,
                token
            );
            updateFormValue(meta.data);
        } else if (Cookies.get('cptt-mock-member')) {
            let member = JSON.parse(Cookies.get('cptt-mock-member'));
            updateFormValue(member);
        } else if (queryParams.length > 1 && queryParams.includes('token')) {
            const indexOfTokenValue = queryParams.indexOf('token') + 1;
            const meta = await getAPI(
                `${process.env.TOKENS_URL}/${encodeURIComponent(queryParams[indexOfTokenValue])}`,
                null,
                token
            );
            updateFormValue(meta.data);
        }
        transaction.end();
    }, []);

    const handleDisclaimers = () => {
        setShowDisclaimers(!showDisclaimers);
    };

    const handleNextMembersPage = async () => {
        sanitizeMemberFormData();
        validateFormFieldsOnSubmit([
            'firstName',
            'lastName',
            'dateOfBirth',
            'memberNumber',
            'groupNumber',
            'disclaimerCheck',
        ]);

        // we first validate the form data
        if (validateFormData()) {
            setLinearProgressDisplay(true);
            const { memberDetails, cpttCorrelationId, errorMessage, planBenefits } =
                await getMemberDetails(formValues);

            if (errorMessage || !memberDetails) {
                handleMemberDetailsErrorSnackbarOpen(errorMessage);
                setLinearProgressDisplay(false);
            } else {
                setCpttCorrelationId(cpttCorrelationId);
                setNoMemberFound(false);
                addMemberDetailsToContext(memberDetails);
                setLinearProgressDisplay(false);
                //Disclaimer check needs to be set to false after each submission, in case a user hits the back button.
                handleChangeCustom(false, 'disclaimerCheck');
                history.push(SEARCH_ROUTE);
                if (planBenefits) {
                    setPlanBenefits(planBenefits);
                }
            }
        }
    };

    /**
     * Updated the logic so that setDisableLookMeUpButton will be triggered whenever form data is changed.
     * This is to better keep track of when the form data is truly valid so we can enable/disable the button.
     */
    useEffect(() => {
        setDisableLookMeUpButton(!validateFormData());
    }, [validateFormData]);

    /**
     * This is for focusing first name on memberDetailsErrorSnackbarOpen and prevent console errors via useEffect
     */
    useEffect(() => {
        if (document.getElementsByClassName('tds-form__input')?.length > 0) {
            document.getElementsByClassName('tds-form__input')[0].focus();
        }
    }, [memberDetailsErrorSnackbarOpen]);

    /*
     * This function will be the delegate for 2 events on the DatePicker:
     * - onDateChange: Tempo delegate. Fires when the date changes through Calendar
     *                 widget OR user manually types in what the control considers
     *                 to be a valid Date. Tempo sends this as a String
     *                 (they must be wrapping the event themselves and extracting value)
     * - onChange: React delegate. Fires whenever there is any change.
     */
    const handleDateOfBirthChange = (e) => {
        let dob = '';
        if (typeof e === 'string' || e instanceof String) {
            dob = e;
        } else {
            dob = e.target.value;
        }
        // scrub the dob string to ignore non-numeric characters
        dob = dob.replace(/\D/g, '');
        if (dob?.length === 8) {
            const formattedDOB = moment(dob, 'MM-DD-YYYY').format('YYYY-MM-DD');
            // we confirm the date is legitimate so Tempo Datepicker does not cry tears in red
            if (isValidDate(formattedDOB)) {
                handleChangeCustom(formattedDOB, 'dateOfBirth');
            }
        } else {
            /*  
                if the user types either not enough 
                or too many digits for the dob, present it as is 
                but treat it as if they had typed nothing at all 
                to surpress Tempo Datepicker warnings 
            */
            handleChangeCustom('', 'dateOfBirth');
        }
    };

    const renderDOBLabel = () => {
        return (
            <>
                Date of birth <span style={classes.colorRed}>*</span>
            </>
        );
    };

    /**
     * Can be abstracted out later on. Minimum date is per Tempo standards.
     * @param {string} dateOfBirth
     * @returns boolean
     */
    const isValidDate = (dateOfBirth) => {
        return (
            new Date('1900-01-01') <= new Date(dateOfBirth) && new Date(dateOfBirth) <= new Date()
        );
    };

    /**
     * function that determines how to render the DOB datepicker
     * based on whether a default DOB is coming from an integration and needs to be autopopulated
     * @returns correct DatePicker component - cannot have a blank/null value for defaultValue
     */
    const renderDOBInput = () => {
        if (defaultDateOfBirth.value && defaultDateOfBirth.value !== '') {
            return (
                <div key={defaultDateOfBirth.value}>
                    <DatePicker
                        name="dateOfBirth"
                        label={renderDOBLabel()}
                        instructionText=""
                        value={dateOfBirth.value}
                        defaultValue={
                            defaultDateOfBirth.value !== '' ? defaultDateOfBirth.value : undefined
                        }
                        max={currentDate}
                        data-testid="dateOfBirth-input"
                        invalidDateErrorText={!!noMemberFound ? '' : 'The date is invalid'}
                        onDateChange={(e) => {
                            handleDateOfBirthChange(e);
                        }}
                        onChange={(e) => handleDateOfBirthChange(e)}
                        state="default"
                    />
                </div>
            );
        } else {
            return (
                <DatePicker
                    name="dateOfBirth"
                    label={renderDOBLabel()}
                    instructionText=""
                    value={dateOfBirth.value}
                    defaultValue={dateOfBirth.value !== '' ? dateOfBirth.value : undefined}
                    max={currentDate}
                    data-testid="dateOfBirth-input"
                    invalidDateErrorText={!!noMemberFound ? '' : 'The date is invalid'}
                    onDateChange={(e) => {
                        handleDateOfBirthChange(e);
                    }}
                    onChange={(e) => handleDateOfBirthChange(e)}
                    state="default"
                />
            );
        }
    };

    const disclaimerLink = () => {
        return (
            <span style={classes.marginBottom10}>
                I have read and understand{' '}
                <Typography.Link
                    role="button"
                    aria-haspopup="dialog"
                    data-testid="termsConditionsLink"
                    onClick={(e) => {
                        e.preventDefault();
                        handleDisclaimers();
                    }}
                    onKeyDown={(e) => {
                        if (e.key == 'Enter' || e.key == ' ') {
                            handleDisclaimers();
                        }
                    }}
                    tabIndex="0"
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                    important information about my estimate.
                </Typography.Link>
            </span>
        );
    };

    const handleMemberDetailsErrorSnackbarClose = () => {
        setMemberDetailsErrorSnackbarOpen(false);
        setMemberDetailsErrorMessage('');
    };

    const handleMemberDetailsErrorSnackbarOpen = async (errorMessage) => {
        await setMemberDetailsErrorMessage(errorMessage);
        await setMemberDetailsErrorSnackbarOpen(true);

        setNoMemberFound(true);
    };

    const renderMemberDetailsErrorSnackbar = () => {
        const snackbarProps = {
            variant: 'error',
            message: memberDetailsErrorMessage,
            vertical: 'top',
            horizontal: 'center',
            open: memberDetailsErrorSnackbarOpen,
            duration: 5000,
            onCloseFunc: handleMemberDetailsErrorSnackbarClose,
        };
        return <GeneralSnackBar snackbar={snackbarProps} />;
    };

    const goToOptumRx = () => {
        const url = process.env.OPTUM_RX_URL;
        window.open(url, '_blank');
    };

    return (
        <div data-testid="memberPage" ref={ref}>
            <LinearIndeterminateProgress displayProgress={linearProgressDisplay} />
            <Typography.H1
                align="center"
                style={{ paddingTop: '15px', paddingBottom: '25px', paddingLeft: '0px' }}
            >
                Estimate your cost for upcoming care
            </Typography.H1>
            <Divider sx={classes.divider} data-testid="headerDivider"></Divider>
            <Grid container direction="column" justifyContent="space-around" alignItems="center">
                <Card
                    variant="outlined"
                    sx={{
                        minWidth: 275,
                        maxWidth: 525,
                        paddingTop: '30px',
                        paddingBottom: '100px',
                        paddingX: '25px',
                        boxShadow: 2,
                        marginTop: '40px',
                    }}
                >
                    <Grid container justifyContent="center">
                        <Grid item>
                            <div>
                                <Typography.H6
                                    style={{
                                        color: '#000000',
                                    }}
                                >
                                    Enter your information to get started{' '}
                                </Typography.H6>
                            </div>
                        </Grid>
                        <Grid item>
                            <div>
                                <Typography.Paragraph>
                                    Your member information is found on your member ID card. We need
                                    this information to look you up.
                                </Typography.Paragraph>
                            </div>
                        </Grid>
                    </Grid>
                    <br></br>
                    <Form.Group alignment="horizontal">
                        <Grid
                            container
                            rowSpacing={1}
                            columnSpacing={2}
                            marginLeft="0px"
                            justifyContent="left"
                            paddingRight="0px"
                        >
                            <Grid item>
                                <Form>
                                    <Form.Group
                                        state={
                                            !!firstName.error || !!noMemberFound ? 'error' : null
                                        }
                                    >
                                        <Form.Label>
                                            First Name <span style={classes.colorRed}>*</span>
                                        </Form.Label>
                                        <Form.Input
                                            label="Enter member first name"
                                            name="firstName"
                                            value={firstName.value}
                                            onChange={(e) => handleChange(e, null, firstName.error)}
                                            required={firstName.required}
                                            data-testid="firstName-input"
                                            sx={classes.marginTop10}
                                            aria-busy={noMemberFound}
                                        />
                                        <Form.ErrorMessage data-testid="firstName-error">
                                            {firstName.error}
                                        </Form.ErrorMessage>
                                    </Form.Group>
                                </Form>
                            </Grid>
                            <Grid item>
                                <Form>
                                    <Form.Group
                                        state={!!lastName.error || !!noMemberFound ? 'error' : null}
                                    >
                                        <Form.Label>
                                            Last Name <span style={classes.colorRed}>*</span>
                                        </Form.Label>
                                        <Form.Input
                                            label="Enter member last name"
                                            name="lastName"
                                            value={lastName.value}
                                            onChange={(e) => handleChange(e, null, lastName.error)}
                                            required={lastName.required}
                                            data-testid="lastName-input"
                                            sx={classes.marginTop10}
                                        />
                                        <Form.ErrorMessage data-testid="lastName-error">
                                            {lastName.error}
                                        </Form.ErrorMessage>
                                    </Form.Group>
                                </Form>
                            </Grid>

                            <Grid item xs={12}>
                                <Form.Group
                                    state={!!dateOfBirth.error || !!noMemberFound ? 'error' : null}
                                >
                                    {renderDOBInput()}
                                </Form.Group>
                            </Grid>
                            <Grid item>
                                <Form>
                                    <Form.Group
                                        state={
                                            !!memberNumber.error || !!noMemberFound ? 'error' : null
                                        }
                                    >
                                        <Form.Label>
                                            Member ID Number <span style={classes.colorRed}>*</span>
                                        </Form.Label>
                                        <Form.Input
                                            label="Enter member number"
                                            name="memberNumber"
                                            value={memberNumber.value}
                                            onChange={(e) =>
                                                handleChange(e, null, memberNumber.error)
                                            }
                                            required={memberNumber.required}
                                            data-testid="memberNumber-input"
                                            sx={classes.marginTop10}
                                        />
                                        <Form.ErrorMessage>{memberNumber.error}</Form.ErrorMessage>
                                    </Form.Group>
                                </Form>
                            </Grid>
                            <Grid item>
                                <Form>
                                    <Form.Group
                                        state={
                                            !!groupNumber.error || !!noMemberFound ? 'error' : null
                                        }
                                    >
                                        <Form.Label>
                                            Group ID Number <span style={classes.colorRed}>*</span>
                                        </Form.Label>
                                        <Form.Input
                                            label="Enter group number"
                                            name="groupNumber"
                                            value={groupNumber.value}
                                            required={groupNumber.required}
                                            data-testid="groupNumber-input"
                                            sx={classes.marginTop10}
                                            onChange={(e) =>
                                                handleChange(e, null, groupNumber.error)
                                            }
                                        />
                                        <Form.ErrorMessage>{groupNumber.error}</Form.ErrorMessage>
                                    </Form.Group>
                                </Form>
                            </Grid>
                            <Grid item xs={12}>
                                <Form.Group
                                    state={
                                        !!disclaimerCheck.error || !!noMemberFound ? 'error' : null
                                    }
                                >
                                    <Form.Checkbox
                                        id="termsConditions"
                                        data-testid="termsConditions"
                                        label={disclaimerLink()}
                                        name="disclaimerCheck"
                                        required={disclaimerCheck.required}
                                        onChange={(e) => {
                                            setCheckboxSelected(e.target.checked);
                                            handleChangeCustom(e.target.checked, 'disclaimerCheck');
                                        }}
                                        state={
                                            submitButtonClicked && disclaimerCheck.error
                                                ? 'error'
                                                : 'default'
                                        }
                                        value={checkboxSelected}
                                    />
                                    <div style={{ marginTop: '10px' }}>
                                        <Typography.Footnote style={{ fontStyle: 'italic' }}>
                                            You are voluntarily entering your information into this
                                            site to obtain the cost estimate. Please note: if you
                                            logged into this site through your member portal or
                                            company sponsored portal, your information may already
                                            be populated.
                                        </Typography.Footnote>
                                    </div>
                                    {!!disclaimerCheck.error && (
                                        <Form.ErrorMessage>
                                            This checkbox is required
                                        </Form.ErrorMessage>
                                    )}
                                </Form.Group>
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setSubmitButtonClicked(true);
                                        setDisableLookMeUpButton(true);
                                        handleChangeCustom(null, 'selectedService');
                                        handleChangeCustom(null, 'selectedProvider');
                                        await handleNextMembersPage();
                                    }}
                                    buttonType={disableLookMeUpButton ? 'disabled' : 'primary-one'}
                                    data-testid="lookMeUpButton"
                                    aria-disabled={disableLookMeUpButton}
                                >
                                    Look me up
                                </Button>
                            </Grid>
                        </Grid>
                        <br></br>
                    </Form.Group>
                    <DisclaimerModal
                        showDisclaimers={showDisclaimers}
                        handleDisclaimers={handleDisclaimers}
                    />
                </Card>
                {rxToggle && (
                    <Card
                        variant="outlined"
                        sx={{
                            maxWidth: 525,
                            minWidth: 275,
                            paddingTop: '16px',
                            paddingBottom: '20px',
                            paddingX: '25px',
                            boxShadow: 2,
                            marginTop: '40px',
                        }}
                    >
                        <Grid container direction={isDesktopView ? 'row' : 'column'}>
                            <Grid container item direction="row">
                                <Grid
                                    item
                                    xs={2}
                                    alignItems="center"
                                    style={{
                                        marginTop: isDesktopView && '67px',
                                        position: isDesktopView && 'absolute',
                                        marginLeft: isDesktopView && '-12px',
                                    }}
                                >
                                    <BrandIcon
                                        icon={IconPillBot}
                                        size={isDesktopView ? 'LARGE' : 'EXTRA_SMALL'}
                                        styleVariation="TWO_TONE_LIGHT_CIRCLE"
                                    />
                                </Grid>
                                <Grid item xs={!isDesktopView ? 8 : 10}>
                                    {isDesktopView ? (
                                        <Typography.H4
                                            style={{
                                                color: '#002677',
                                                paddingLeft: '5rem',
                                                marginBottom: '0.5rem',
                                            }}
                                        >
                                            Is OptumRX your pharmacy?
                                        </Typography.H4>
                                    ) : (
                                        <Typography.H6
                                            style={{ color: '#002677', marginBottom: '1rem' }}
                                        >
                                            Is OptumRX your pharmacy?
                                        </Typography.H6>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid container item style={{ paddingLeft: isDesktopView && '5rem' }}>
                                <Grid item xs={isDesktopView && 10}>
                                    <Typography.Paragraph
                                        style={{ fontSize: !isDesktopView && '12.64px' }}
                                    >
                                        Optum RX can provide you with a Pharmacy & Prescription
                                        estimate, allow you to manage your prescriptions online, and
                                        receive free home delivery.
                                    </Typography.Paragraph>
                                </Grid>
                                <Grid item xs={isDesktopView && 11}>
                                    <Typography.Paragraph
                                        style={{ fontSize: isDesktopView ? '14.22px' : '12.64px' }}
                                    >
                                        Check for your Optum RX coverage on your member ID Card.
                                    </Typography.Paragraph>
                                </Grid>
                                <Grid item>
                                    <Button
                                        style={{
                                            width: '210px',
                                            borderRadius: '24px',
                                        }}
                                        role="link"
                                        aria-label="Go to Optum RX, opens in a new tab or window"
                                        data-testid="gotoOptumRxButton"
                                        onClick={() => goToOptumRx()}
                                    >
                                        Go to Optum RX
                                        <Launch
                                            style={{
                                                height: '15px',
                                                width: '15px',
                                                marginLeft: '5px',
                                                position: 'absolute',
                                                marginTop: '4px',
                                            }}
                                        />
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Card>
                )}
                {renderMemberDetailsErrorSnackbar()}
            </Grid>
        </div>
    );
};

export default MemberPage;
